import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider
} from '@mui/material';
import { toast } from 'react-toastify';
import apiService from '../services/api';

interface CRColumnMappingModalProps {
  open: boolean;
  onClose: () => void;
  s3Key: string;
  availableColumns: string[];
  missingColumns: string[];
  onMappingComplete: (mappedS3Key: string) => void;
}

const REQUIRED_COLUMNS = [
  { name: 'ItemGrade', description: 'Grade level of the item' },
  { name: 'PassageBody', description: 'Reading passage text' },
  { name: 'ItemPrompt', description: 'Question or prompt text' },
  { name: 'StudentResponse', description: 'Student answer text' },
  { name: 'RubricScale', description: 'Maximum rubric points; AI scoring uses 0..RubricScale' },
  { name: 'StudentID', description: 'Student identifier' },
  { name: 'Teacher', description: 'Teacher name or identifier' },
  { name: 'Item', description: 'Item identifier' }
];

const OPTIONAL_COLUMNS = [
  { name: 'StudentScore', description: 'Teacher/human score (for QWK comparison)' },
  { name: 'Special Education', description: 'Special Education status' }
];

const CRColumnMappingModal: React.FC<CRColumnMappingModalProps> = ({
  open,
  onClose,
  s3Key,
  availableColumns,
  missingColumns,
  onMappingComplete
}) => {
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [isValidating, setIsValidating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [previewData, setPreviewData] = useState<Array<Record<string, any>>>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Initialize mapping with smart suggestions
  useEffect(() => {
    if (open && availableColumns.length > 0) {
      const smartMapping: Record<string, string> = {};

      // Try to auto-map columns based on similar names
      REQUIRED_COLUMNS.forEach(({ name }) => {
        const lowerName = name.toLowerCase();
        const matchingColumn = availableColumns.find(col => {
          const lowerCol = col.toLowerCase();
          return (
            lowerCol === lowerName ||
            lowerCol.includes(lowerName.replace(/([A-Z])/g, '_$1').toLowerCase()) ||
            lowerCol.replace(/_/g, '').includes(lowerName.replace(/([A-Z])/g, '').toLowerCase())
          );
        });

        if (matchingColumn) {
          smartMapping[name] = matchingColumn;
        }
      });

      setColumnMapping(smartMapping);
      setShowPreview(false);
      setPreviewData([]);
      setValidationError(null);
    }
  }, [open, availableColumns]);

  const handleMappingChange = (requiredColumn: string, sourceColumn: string) => {
    setColumnMapping(prev => ({
      ...prev,
      [requiredColumn]: sourceColumn
    }));
    setShowPreview(false);
    setPreviewData([]);
    setValidationError(null);
  };

  const handlePreview = async () => {
    // Check if all required columns are mapped
    const unmappedRequired = REQUIRED_COLUMNS.filter(col => !columnMapping[col.name]);
    if (unmappedRequired.length > 0) {
      toast.error(`Please map all required columns: ${unmappedRequired.map(c => c.name).join(', ')}`);
      return;
    }

    setIsValidating(true);
    setValidationError(null);

    try {
      const response = await apiService.validateCRColumnMapping(s3Key, columnMapping);

      if (response.valid) {
        setPreviewData(response.preview_data || []);
        setShowPreview(true);
        toast.success('Mapping validated successfully!');
      } else {
        setValidationError(response.errors?.join(', ') || 'Validation failed');
        toast.error('Mapping validation failed');
      }
    } catch (error: any) {
      setValidationError(error.message || 'Failed to validate mapping');
      toast.error('Failed to validate mapping');
    } finally {
      setIsValidating(false);
    }
  };

  const handleSave = async () => {
    if (!showPreview) {
      toast.warning('Please preview the mapping first');
      return;
    }

    setIsSaving(true);

    try {
      const response = await apiService.saveCRColumnMapping(s3Key, columnMapping);

      if (response.status === 'success') {
        toast.success('Column mapping saved successfully!');
        onMappingComplete(response.mapped_s3_key);
        onClose();
      } else {
        toast.error('Failed to save mapping');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to save mapping');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setColumnMapping({});
    setShowPreview(false);
    setPreviewData([]);
    setValidationError(null);
    onClose();
  };

  const getUnmappedRequiredCount = () => {
    return REQUIRED_COLUMNS.filter(col => !columnMapping[col.name]).length;
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h5">Column Mapping</Typography>
        <Typography variant="body2" color="text.secondary">
          Map your dataset columns to the required CR schema
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {validationError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {validationError}
          </Alert>
        )}

        {/* Required Columns Mapping */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Required Columns
          </Typography>
          <Grid container spacing={2}>
            {REQUIRED_COLUMNS.map(column => (
              <Grid item xs={12} key={column.name}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" fontWeight="medium">
                      {column.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {column.description}
                    </Typography>
                  </Box>

                  <Typography variant="body1" sx={{ mx: 2 }}>
                    →
                  </Typography>

                  <FormControl sx={{ flex: 1 }} size="small">
                    <InputLabel>Source Column</InputLabel>
                    <Select
                      value={columnMapping[column.name] || ''}
                      onChange={(e) => handleMappingChange(column.name, e.target.value)}
                      label="Source Column"
                    >
                      <MenuItem value="">
                        <em>Select column</em>
                      </MenuItem>
                      {availableColumns.map(col => (
                        <MenuItem key={col} value={col}>
                          {col}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Optional Columns Mapping */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Optional Columns
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These columns are not required but enable additional features like QWK analysis
          </Typography>
          <Grid container spacing={2}>
            {OPTIONAL_COLUMNS.map(column => (
              <Grid item xs={12} key={column.name}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" fontWeight="medium">
                      {column.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {column.description}
                    </Typography>
                  </Box>

                  <Typography variant="body1" sx={{ mx: 2 }}>
                    →
                  </Typography>

                  <FormControl sx={{ flex: 1 }} size="small">
                    <InputLabel>Source Column</InputLabel>
                    <Select
                      value={columnMapping[column.name] || ''}
                      onChange={(e) => handleMappingChange(column.name, e.target.value)}
                      label="Source Column"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {availableColumns.map(col => (
                        <MenuItem key={col} value={col}>
                          {col}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Preview Section */}
        {showPreview && previewData.length > 0 && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box>
              <Typography variant="h6" gutterBottom>
                Preview (First 5 Rows)
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {REQUIRED_COLUMNS.map(col => (
                        <TableCell key={col.name}>
                          <strong>{col.name}</strong>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.map((row, idx) => (
                      <TableRow key={idx}>
                        {REQUIRED_COLUMNS.map(col => (
                          <TableCell key={col.name}>
                            {String(row[col.name] || '').substring(0, 50)}
                            {String(row[col.name] || '').length > 50 && '...'}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </>
        )}

        {/* Mapping Status */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="body2">
            <strong>Mapping Status:</strong>{' '}
            {getUnmappedRequiredCount() === 0 ? (
              <span style={{ color: 'green' }}>
                ✓ All required columns mapped
              </span>
            ) : (
              <span style={{ color: 'orange' }}>
                {getUnmappedRequiredCount()} required column(s) unmapped
              </span>
            )}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} disabled={isValidating || isSaving}>
          Cancel
        </Button>
        <Button
          onClick={handlePreview}
          disabled={isValidating || isSaving || getUnmappedRequiredCount() > 0}
          variant="outlined"
        >
          {isValidating ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          Preview Mapping
        </Button>
        <Button
          onClick={handleSave}
          disabled={!showPreview || isSaving}
          variant="contained"
          color="primary"
        >
          {isSaving ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          Save Mapping
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CRColumnMappingModal;
